import type { MissionAssessmentListItemDto } from '@@types/api.types';
import { TECHNOLOGY_TAB_HISTORY_LIST_CLASS } from '@components/pages/technology/components/shared/technology-container-query.constants';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { usePrefetchMissionAssessmentRequest } from '@queries/mission-assessment-requests.query';
import { usePrefetchMissionAssessment } from '@queries/mission-assessments.query';
import { LayoutDashboard } from 'lucide-react';
import { FC, Fragment, ReactNode, useMemo } from 'react';

export type MaSidebarAssessmentListRenderItemParams = {
  isSelected: boolean;
  onPrefetch: () => void;
  onSelect: () => void;
  version: MissionAssessmentListItemDto;
};

type MaSidebarAssessmentListProps = {
  emptyText: string;
  isLoading?: boolean;
  isOverviewActive: boolean;
  onSelectAssessment: (assessmentId: string) => void;
  onSelectOverview: () => void;
  renderItem: (params: MaSidebarAssessmentListRenderItemParams) => ReactNode;
  selectedAssessmentId: string | null;
  title: string;
  titleClassName?: string;
  versions: MissionAssessmentListItemDto[];
};

const splitVersions = (
  versions: MissionAssessmentListItemDto[],
): { latestVersion?: MissionAssessmentListItemDto; otherVersions: MissionAssessmentListItemDto[] } => {
  if (versions.length === 0) {
    return { latestVersion: undefined, otherVersions: [] };
  }

  const [latestVersion, ...otherVersions] = versions;

  return {
    latestVersion,
    otherVersions,
  };
};

const isSelectableVersion = (version: MissionAssessmentListItemDto): boolean => {
  return !version.accessRestricted;
};

export const MaSidebarAssessmentList: FC<MaSidebarAssessmentListProps> = (props) => {
  const {
    emptyText,
    isLoading,
    isOverviewActive,
    onSelectAssessment,
    onSelectOverview,
    renderItem,
    selectedAssessmentId,
    title,
    titleClassName,
    versions,
  } = props;

  const prefetchMissionAssessment = usePrefetchMissionAssessment();
  const prefetchRequest = usePrefetchMissionAssessmentRequest();

  const { latestVersion, otherVersions } = useMemo(() => {
    return splitVersions(versions);
  }, [versions]);

  const isLatestSelected = useMemo(() => {
    if (!latestVersion || isOverviewActive) return false;

    return selectedAssessmentId === null || selectedAssessmentId === latestVersion.id;
  }, [isOverviewActive, latestVersion, selectedAssessmentId]);

  const handlePrefetch = (version: MissionAssessmentListItemDto): void => {
    if (!isSelectableVersion(version)) {
      return;
    }

    void prefetchMissionAssessment(version.id);
    void prefetchRequest(version.request?.id);
  };

  const handleSelectVersion = (version: MissionAssessmentListItemDto): void => {
    if (!isSelectableVersion(version)) {
      return;
    }

    onSelectAssessment(version.id);
  };

  return (
    <Tile isLoading={isLoading}>
      <div className="flex flex-col gap-3">
        <button
          className={cn(
            'flex w-full items-center gap-5 rounded-lg px-3 py-2.5 text-left transition-all duration-200',
            isOverviewActive
              ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF26] text-[#3D2EFF]'
              : 'bg-[#F5F5F5] text-[#1F1C1A] hover:ring-2 hover:ring-[#3D2EFFCC]',
          )}
          onClick={onSelectOverview}
          type="button"
        >
          <LayoutDashboard className="ml-2.5 size-6 min-h-10 shrink-0" />
          <span className="text-sm font-medium">MA Overview</span>
        </button>

        {versions.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className={cn('my-1 text-base font-medium text-gray-700', titleClassName)}>{title}</p>

            <div className={TECHNOLOGY_TAB_HISTORY_LIST_CLASS}>
              {latestVersion && (
                <Fragment key={latestVersion.id}>
                  {renderItem({
                    isSelected: isLatestSelected,
                    onPrefetch: () => handlePrefetch(latestVersion),
                    onSelect: () => handleSelectVersion(latestVersion),
                    version: latestVersion,
                  })}
                </Fragment>
              )}

              {otherVersions.map((version) => (
                <Fragment key={version.id}>
                  {renderItem({
                    isSelected: !isOverviewActive && version.id === selectedAssessmentId,
                    onPrefetch: () => handlePrefetch(version),
                    onSelect: () => handleSelectVersion(version),
                    version,
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};
